import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';

const SETTINGS_DOC = 'siteSettings';
const SETTINGS_COLLECTION = 'settings';
const STORAGE_PATH = 'assets/profile';

// Default settings (fallback values)
const DEFAULT_SETTINGS = {
  profileImageUrl: '',
  resumePdfUrl: '',
  updatedAt: null
};

export const settingsService = {
  // Get site settings
  async getSettings() {
    try {
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC);
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        return {
          ...DEFAULT_SETTINGS,
          ...settingsDoc.data()
        };
      }

      return DEFAULT_SETTINGS;
    } catch (error) {
      console.error('Error fetching settings:', error);
      return DEFAULT_SETTINGS;
    }
  },

  // Update site settings
  async updateSettings(updates) {
    try {
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC);
      await setDoc(settingsRef, {
        ...updates,
        updatedAt: new Date()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  },

  // Upload profile image
  async uploadProfileImage(file) {
    try {
      const fileName = `portrait.${file.name.split('.').pop()}`;
      const storageRef = ref(storage, `${STORAGE_PATH}/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading profile image:', error);
      throw error;
    }
  },

  // Upload resume PDF
  async uploadResumePdf(file) {
    try {
      const fileName = `resume.pdf`;
      const storageRef = ref(storage, `assets/documents/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading resume PDF:', error);
      throw error;
    }
  },

  // Delete file from storage by URL
  async deleteFileByUrl(url) {
    try {
      if (!url) return;

      const storagePath = this.getStoragePathFromUrl(url);
      if (storagePath) {
        const fileRef = ref(storage, storagePath);
        await deleteObject(fileRef);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      // Don't throw - file might already be deleted
    }
  },

  // Extract storage path from Firebase URL
  getStoragePathFromUrl(url) {
    try {
      if (!url) return null;

      const urlObj = new URL(url);
      const pathParam = urlObj.searchParams.get('name') || urlObj.pathname.split('/o/')[1]?.split('?')[0];

      if (pathParam) {
        return decodeURIComponent(pathParam);
      }

      const pathParts = urlObj.pathname.split('/o/');
      if (pathParts.length > 1) {
        return decodeURIComponent(pathParts[1].split('?')[0]);
      }

      return null;
    } catch (error) {
      console.error('Error extracting storage path from URL:', error);
      return null;
    }
  }
};
